import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface UserType {
  usertypeid: number;
  usertypename: string;
  description: string;
  status: string;
  createdon: string;
  createdby: string;
}

export interface Roles {
  roleid: number;
  rolename: string;
  description: string;
  status: string;
  createdon: string;
  createdby: string;
}

export interface Permission {
  permissionid: number;
  permissionname: string;
  description: string;
  status: string;
  createdon: string;
  createdby: string;
}

export interface RolePermission {
  rolepermissionid: number;
  permissionname: string;
  rolename: string;
  status: string;
  createdon: string;
  createdby: string;
}

export interface AdminUser {
  usermasterid: number;
  username: string;
  email_id: string;
  phone_no: string;
  application_name: string;
  user_type_name: string;
  status: string;
  createdon: string;
  createdby: string;
  updatedon: string | null;
  updatedby: string | null;
}

export interface UserRole {
  userroleid: number;
  username: string;
  email_id: string;
  phone_no: string;
  rolename: string;
  status: string;
  createdon: string;
  createdby: string;
}

export interface Application {
  applicationid: number;
  organisationid: number;
  applicationtoken: string;
  applicationname: string;
  applicationdescription: string;
  tokencreateddate: string;
  tokenexpiredate: string;
  status: string;
  createdon: string;
  createdby: string;
}

export interface Menu {
  menuid: number;
  moduleiid: number;
  parentid: number;
  applicationname: string;
  modulename: string;
  title: string;
  description: string;
  routepath: string;
  displayorder: number;
  status: string;
  createdon: string;
  createdby: string;
  updatedon: string | null;
  updatedby: string | null;
}

export interface Module {
  moduleid: number;
  applicationid: number;
  applicationname: string;
  modulename: string;
  description: string;
  status: string;
  createdon: string;
  createdby: string;
}

export interface Task {
  taskid: number;
  taskname: string;
  description: string;
  status: string;
  createdon: string;
  createdby: string;
}

export interface RoleTask {
  id: number;
  roleid: number;
  taskid: number;
  rolename: string;
  taskname: string;
  status: string;
  createdon: string;
  createdby: string;
}

export interface MenuPermission {
  menupermissionid: number;
  menuid: number;
  permissionid: number;
  applicationname: string;
  modulename: string;
  menuname: string;
  permissionname: string;
  status: string;
  createdon: string;
  createdby: string;
}

export interface RoleMenu {
  role_menuid?: number; 
  id?: number; 
  roleid: number;
  menuid: number;
  rolename: string;
  menuname: string;
  createdon: string;
  createdby: string;
}

export interface UserMenuSubMenu {
  menuid: number;
  displayorder: number;
  title: string;
  routepath: string;
  parentid: number;
  submenu?: UserMenuSubMenu[];
}

export interface UserMenu {
  menuid: number;
  displayorder: number;
  title: string;
  routepath: string;
  parentid: number;
  submenu?: UserMenuSubMenu[];
}

interface RBACState {
  userTypes: UserType[];
  roles: Roles[];
  permissions: Permission[];
  rolePermissions: RolePermission[];
  adminUsers: AdminUser[];
  adminUsersPagination: {
    page: number;
    per_page: number;
    total_pages: number;
    total_items: number;
  } | null;
  userRoles: UserRole[];
  applications: Application[];
  menus: Menu[];
  modules: Module[];
  tasks: Task[];
  roleTasks: RoleTask[];
  menuPermissions: MenuPermission[];
  roleMenus: RoleMenu[];
  userMenus: UserMenu[];

  rbacSuccessMsg: {
    message: string;
    id: string | number;
  };

  rbacErrorMsg: {
    message: string;
    id: string | number;
  };

  loading: boolean;
}

export const initialState: RBACState = {
  userTypes: [],
  roles: [],
  permissions: [],
  rolePermissions: [],
  adminUsers: [],
  adminUsersPagination: null,
  userRoles: [],
  applications: [],
  menus: [],
  modules: [],
  tasks: [],
  roleTasks: [],
  menuPermissions: [],
  roleMenus: [],
  userMenus: [],

  rbacSuccessMsg: { message: "", id: "" },
  rbacErrorMsg: { message: "", id: "" },

  loading: false,
};

const rbacSlice = createSlice({
  name: "rbac",
  initialState,
  reducers: {
    fetchUserTypes(state, action: PayloadAction<UserType[]>) {
      state.userTypes = action.payload || [];
    },

    fetchRoles(state, action: PayloadAction<Roles[]>) {
      state.roles = action.payload || [];
    },

    fetchPermissions(state, action: PayloadAction<Permission[]>) {
      state.permissions = action.payload || [];
    },

    fetchRolePermissions(state, action: PayloadAction<RolePermission[]>) {
      state.rolePermissions = action.payload || [];
    },

    fetchAdminUsers(state, action: PayloadAction<{ users: AdminUser[]; pagination: any }>) {
      state.adminUsers = action.payload.users || [];
      state.adminUsersPagination = action.payload.pagination || null;
    },

    fetchUserRoles(state, action: PayloadAction<UserRole[]>) {
      state.userRoles = action.payload || [];
    },

    fetchApplications(state, action: PayloadAction<Application[]>) {
      state.applications = action.payload || [];
    },

    fetchMenus(state, action: PayloadAction<Menu[]>) {
      state.menus = action.payload || [];
    },

    fetchModules(state, action: PayloadAction<Module[]>) {
      state.modules = action.payload || [];
    },

    fetchTasks(state, action: PayloadAction<Task[]>) {
      state.tasks = action.payload || [];
    },

    fetchRoleTasks(state, action: PayloadAction<RoleTask[]>) {
      state.roleTasks = action.payload || [];
    },

    fetchMenuPermissions(state, action: PayloadAction<MenuPermission[]>) {
      state.menuPermissions = action.payload || [];
    },

    fetchRoleMenus(state, action: PayloadAction<RoleMenu[]>) {
      state.roleMenus = action.payload || [];
    },

    fetchUserMenus(state, action: PayloadAction<UserMenu[]>) {
      state.userMenus = action.payload || [];
    },

    rbacSuccessMsg(state, action: PayloadAction<any>) {
      state.rbacSuccessMsg = action.payload;
    },

    rbacErrorMsg(state, action: PayloadAction<any>) {
      state.rbacErrorMsg = action.payload;
    },

    setRbacLoader(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
  },
});

export const {
  fetchUserTypes,
  fetchRoles,
  fetchPermissions,
  fetchRolePermissions,
  fetchAdminUsers,
  fetchUserRoles,
  fetchApplications,
  fetchMenus,
  fetchModules,
  fetchTasks,
  fetchRoleTasks,
  fetchMenuPermissions,
  fetchRoleMenus,
  fetchUserMenus,
  rbacSuccessMsg,
  rbacErrorMsg,
  setRbacLoader,
} = rbacSlice.actions;

export default rbacSlice.reducer;

